'use client';
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import Select from 'react-select';
import api from '../../../domain/api';
import { useSelectAllFacebookAccount } from '../../../shared/hooks/UseFacebookAccount';
// import AIChatbot from './ai-chatbot';

const fetchReports = async ({ queryKey }) => {
  const [, filters] = queryKey;
  const { account, ...rest } = filters;
  const payload = {
    ...rest,
    ...(account ? { accountId: account } : {}),
  };
  const res = await api.post('/facebook-ads/campaign-insights', payload);
  return res.data;
};

const updateBenchmark = async (accountId, campaignId, benchmark) => {
  try {
    const res = await api.post('/benchmarks/upsert', {
      accountId,
      campaign_id: campaignId,
      benchmark,
    });
    return res.data;
  } catch (error) {
    console.error('Error updating benchmark:', error);
    throw error;
  }
};

const EnhancedFacebookAdsTable = () => {
  const queryClient = useQueryClient();
  const [accountData, isAccountsLoading] = useSelectAllFacebookAccount();
  const [selectedAccountDetails, setSelectedAccountDetails] = useState(null);
  const [benchmarkModal, setBenchmarkModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [benchmarkValue, setBenchmarkValue] = useState('');
  const [isSavingBenchmark, setIsSavingBenchmark] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isAccountBenchmarkEdit, setIsAccountBenchmarkEdit] = useState(false);

  // Read initial state from URL search params
  const getInitialFromSearch = () => {
    const params = new URLSearchParams(window.location.search);
    const account = params.get('accountId') || '';
    const rangeType = params.get('rangeType') || 'yesterday';
    const from = params.get('from') || '';
    const to = params.get('to') || '';
    return {
      account,
      dateFilter: { rangeType, from, to },
    };
  };

  const initial = getInitialFromSearch();

  const accountOptions = useMemo(() => {
    if (
      !accountData ||
      !accountData.adAccounts ||
      !Array.isArray(accountData.adAccounts)
    ) {
      return [];
    }
    return accountData.adAccounts.map((account) => ({
      label: account.accountName,
      value: account.accountId,
    }));
  }, [accountData]);

  const [account, setAccount] = useState(initial.account);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const [dateFilter, setDateFilter] = useState(initial.dateFilter);

  // Persist to URL search params when account or dateFilter changes
  useEffect(() => {
    // Skip URL updates for custom range - handle separately
    if (dateFilter.rangeType === 'custom') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (account) {
      params.set('accountId', account);
    } else {
      params.delete('accountId');
    }

    // For preset ranges, update immediately
    if (dateFilter.rangeType) {
      params.set('rangeType', dateFilter.rangeType);
    } else {
      params.delete('rangeType');
    }
    // Clear any existing custom date params when switching to presets
    params.delete('from');
    params.delete('to');

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [account, dateFilter]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'facebook-ads-reports',
      {
        account,
        sortBy: sorting[0]?.id,
        sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        rangeType: dateFilter.rangeType,
        from: dateFilter.from,
        to: dateFilter.to,
      },
    ],
    queryFn: fetchReports,
    keepPreviousData: true,
    enabled: (() => {
      const isEnabled = Boolean(
        account &&
          (dateFilter.rangeType !== 'custom' ||
            (Boolean(dateFilter.from) && Boolean(dateFilter.to)))
      );
      console.log('Query enabled:', isEnabled, {
        account,
        rangeType: dateFilter.rangeType,
        from: dateFilter.from,
        to: dateFilter.to,
      });
      return isEnabled;
    })(),
  });

  // Function to handle custom date range application
  const applyCustomDateFilter = () => {
    if (!dateFilter.from || !dateFilter.to) {
      return;
    }

    // Update URL parameters for custom range
    const params = new URLSearchParams(window.location.search);
    if (account) {
      params.set('accountId', account);
    }
    params.set('rangeType', 'custom');
    params.set('from', String(dateFilter.from));
    params.set('to', String(dateFilter.to));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);

    // Force query refetch by invalidating the query
    queryClient.invalidateQueries(['facebook-ads-reports']);
  };

  // Helper function to determine if a row should be highlighted
  const shouldHighlightRow = (rowData) => {
    const cpl = parseFloat(rowData.cost_per_lead) || 0;
    const campaignBenchmark = parseFloat(rowData.benchmark) || null;
    const clientBenchmark =
      parseFloat(selectedAccountDetails?.fb_benchmark) || null; // Fixed: use fb_benchmark

    // Use campaign benchmark if available, otherwise use client benchmark
    // If neither exists, don't highlight
    const effectiveBenchmark = campaignBenchmark || clientBenchmark;

    // Highlight if CPL is 0 OR if effective benchmark exists and CPL exceeds it
    return cpl === 0 || (effectiveBenchmark && cpl > effectiveBenchmark);
  };

  const getRowHighlightClass = (rowData) => {
    if (!shouldHighlightRow(rowData)) return '';

    const cpl = parseFloat(rowData.cost_per_lead) || 0;

    // Red for zero CPL (no leads), orange for exceeding benchmark
    return cpl === 0 ? 'row-highlight-red' : 'row-highlight-orange';
  };

  const columns = useMemo(
    () => [
      // {
      //   header: 'Off/On',
      //   accessorKey: 'status',
      //   cell: ({ row }) => (
      //     <div className='toggle-container'>
      //       <label className='toggle-switch'>
      //         <input
      //           type='checkbox'
      //           checked={row.original.status === 'active'}
      //           onChange={() => console.log('Toggle status', row.original)}
      //         />
      //         <span className='toggle-slider'></span>
      //       </label>
      //     </div>
      //   ),
      // },
      {
        header: 'Campaign',
        accessorKey: 'campaign_name',
        cell: ({ getValue, row }) => (
          <div className='campaign-cell'>
            <button
              type='button'
              className='campaign-link'
              title={getValue() || row.original.campaign_id}
              onClick={() => console.log('Campaign clicked:', row.original)}
            >
              {getValue() || row.original.campaign_id}
            </button>
          </div>
        ),
      },
      {
        header: 'Benchmark',
        accessorKey: 'benchmark',
        cell: ({ getValue, row }) => {
          const benchmark = getValue() || row.original.benchmark;
          const isEmpty = !benchmark;
          return (
            <div className='benchmark-cell'>
              <button
                type='button'
                className={`benchmark-edit-button ${
                  isEmpty ? 'benchmark-empty' : ''
                }`}
                onClick={() => {
                  setBenchmarkModal({
                    isOpen: true,
                    campaign: row.original,
                  });
                  setBenchmarkValue(benchmark || '');
                }}
                title='Click to edit benchmark'
              >
                <span className='benchmark-display'>
                  <i className='fa fa-target benchmark-icon'></i>
                  <span className='benchmark-value'>
                    {benchmark || 'Set benchmark'}
                  </span>
                </span>
                <i className='fa fa-edit edit-action-icon'></i>
              </button>
            </div>
          );
        },
      },
      {
        header: 'Campaign ID',
        accessorKey: 'campaign_id',
        cell: ({ getValue }) => (
          <div className='campaign-id-cell' title={getValue()}>
            {getValue()}
          </div>
        ),
      },
      {
        header: 'Impressions',
        accessorKey: 'impressions',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'Clicks',
        accessorKey: 'clicks',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'CTR (%)',
        accessorKey: 'ctr',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseFloat(getValue()).toFixed(2) + '%' : '—'}
          </div>
        ),
      },
      {
        header: 'Spend',
        accessorKey: 'spend',
        cell: ({ getValue }) => (
          <div className='budget-cell'>
            ₹{getValue() ? Number.parseFloat(getValue()).toFixed(2) : '—'}
          </div>
        ),
      },
      {
        header: 'Leads',
        accessorKey: 'leads',
        cell: ({ getValue }) => (
          <div className='results-cell'>
            <div className='result-number'>{getValue() || '0'}</div>
            <div className='result-type'>Meta leads</div>
          </div>
        ),
      },
      {
        header: 'Cost per Lead',
        accessorKey: 'cost_per_lead',
        cell: ({ getValue, row }) => {
          const cpl = parseFloat(getValue()) || 0;
          const campaignBenchmark = parseFloat(row.original.benchmark) || null;
          const clientBenchmark =
            parseFloat(selectedAccountDetails?.fb_benchmark) || null; // Fixed: use fb_benchmark

          // Use campaign benchmark if available, otherwise use client benchmark
          // If neither exists, don't highlight
          const effectiveBenchmark = campaignBenchmark || clientBenchmark;

          // Determine if this CPL should be highlighted
          const shouldHighlight =
            cpl === 0 || (effectiveBenchmark && cpl > effectiveBenchmark);
          const highlightClass = shouldHighlight
            ? cpl === 0
              ? 'cpl-highlight-red'
              : 'cpl-highlight-orange'
            : '';

          // Debug logging for CPL column
          if (shouldHighlight) {
            console.log('CPL Cell Highlight:', {
              campaignName: row.original.campaign_name,
              cpl,
              campaignBenchmark,
              clientBenchmark,
              effectiveBenchmark,
              reason: cpl === 0 ? 'No leads' : 'Exceeds benchmark',
            });
          }

          return (
            <div className={`cost-cell ${highlightClass}`}>
              <div className='cost-amount'>
                {getValue()
                  ? `₹${Number.parseFloat(getValue()).toFixed(2)}`
                  : '—'}
              </div>
              <div className='cost-type'>Per lead</div>
              {shouldHighlight && (
                <div className='cost-warning'>
                  <i className='fa fa-exclamation-triangle'></i>
                  {cpl === 0 ? 'No leads' : 'Exceeds benchmark'}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [selectedAccountDetails?.fb_benchmark]
  );

  const table = useReactTable({
    data: data?.data || [],
    columns,
    pageCount: data?.totalPages || -1,
    manualPagination: true,
    manualSorting: true,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Ensure account state reflects URL (deep-link) once on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlAccount = params.get('accountId');
    if (urlAccount && !account) {
      setAccount(urlAccount);
    }
  }, []); // run once

  // When account options/data arrive, if account is set but details missing, populate
  useEffect(() => {
    if (account && accountData?.adAccounts && !selectedAccountDetails) {
      const details = accountData.adAccounts.find(
        (a) => String(a.accountId) === String(account)
      );
      if (details) setSelectedAccountDetails(details);
    }
  }, [account, accountData, selectedAccountDetails]);

  // Keep selectedAccountDetails updated if account changes
  useEffect(() => {
    if (account && accountData?.adAccounts) {
      const details = accountData.adAccounts.find(
        (a) => String(a.accountId) === String(account)
      );
      if (details && details !== selectedAccountDetails) {
        setSelectedAccountDetails(details);
      }
    }
  }, [account, accountData]);

  return (
    <>
      <style jsx>{`
        .meta-ads-container {
          background: #f5f6f7;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Helvetica, Arial, sans-serif;
        }

        .meta-ads-header {
          background: linear-gradient(135deg, #1877f2 0%, #166fe5 100%);
          color: white;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 8px rgba(24, 119, 242, 0.15);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }

        .account-selector-header {
          min-width: 280px;
        }

        .meta-ads-content {
          background: white;
          margin: 0;
          border-radius: 0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .toolbar {
          padding: 10px 20px;
          border-bottom: 1px solid #e4e6ea;
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fafbfc;
        }

        .toolbar-button {
          background: #1877f2;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
        }

        .toolbar-button.secondary {
          background: #e4e6ea;
          color: #1c1e21;
        }

        .toolbar-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .search-container {
          margin-left: auto;
          position: relative;
        }

        .search-input {
          padding: 6px 10px 6px 30px;
          border: 1px solid #ccd0d5;
          border-radius: 4px;
          font-size: 13px;
          width: 200px;
        }

        .search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #65676b;
          font-size: 12px;
        }

        .meta-table-container {
          position: relative;
          overflow-x: auto;
          max-height: calc(100vh - 200px);
          border: 1px solid #e4e6ea;
        }

        .meta-table {
          width: 100%;
          min-width: 1400px;
          border-collapse: collapse;
          background: white;
          font-size: 13px;
        }

        .meta-table-header {
          background: #f8f9fa;
          border-bottom: 2px solid #e4e6ea;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .meta-table-header th {
          padding: 8px 12px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          white-space: nowrap;
          border-right: 1px solid #e4e6ea;
        }

        .meta-table-header th:last-child {
          border-right: none;
        }

        .meta-table-header th:hover {
          background: #e9ecef;
        }

        .meta-table-body tr {
          border-bottom: 1px solid #f0f2f5;
        }

        .meta-table-body tr:hover {
          background: #f8f9fa;
        }

        .meta-table-body td {
          padding: 8px 12px;
          font-size: 13px;
          color: #1c1e21;
          vertical-align: top;
          border-right: 1px solid #f0f2f5;
        }

        .meta-table-body td:last-child {
          border-right: none;
        }

        .toggle-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 36px;
          height: 20px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccd0d5;
          transition: 0.3s;
          border-radius: 20px;
        }

        .toggle-slider:before {
          position: absolute;
          content: '';
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #1877f2;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(16px);
        }

        .campaign-cell {
          max-width: 300px;
          min-width: 200px;
          word-wrap: break-word;
          white-space: normal;
        }

        .campaign-link {
          color: #1877f2;
          background: none;
          border: none;
          text-decoration: none;
          font-weight: 500;
          font-size: 13px;
          display: block;
          line-height: 1.3;
          cursor: pointer;
          padding: 0;
          text-align: left;
        }

        .campaign-link:hover {
          text-decoration: underline;
        }

        .benchmark-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 140px;
        }

        .benchmark-edit-button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #495057;
          min-width: 120px;
          position: relative;
          overflow: hidden;
        }

        .benchmark-edit-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transition: left 0.5s ease;
        }

        .benchmark-edit-button:hover::before {
          left: 100%;
        }

        .benchmark-edit-button:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
          border-color: #1877f2;
          color: #1877f2;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(24, 119, 242, 0.15);
        }

        .benchmark-edit-button:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(24, 119, 242, 0.2);
        }

        .benchmark-display {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
        }

        .benchmark-icon {
          color: #6c757d;
          font-size: 12px;
          transition: color 0.2s ease;
        }

        .benchmark-edit-button:hover .benchmark-icon {
          color: #1877f2;
        }

        .benchmark-value {
          font-weight: 500;
          color: inherit;
          font-size: 13px;
        }

        .benchmark-edit-button .benchmark-value {
          color: #495057;
        }

        .benchmark-edit-button:hover .benchmark-value {
          color: #1877f2;
        }

        .edit-action-icon {
          color: #6c757d;
          font-size: 11px;
          margin-left: 8px;
          opacity: 0.7;
          transition: all 0.2s ease;
        }

        .benchmark-edit-button:hover .edit-action-icon {
          color: #1877f2;
          opacity: 1;
          transform: scale(1.1);
        }

        /* Special styling for empty benchmark */
        .benchmark-edit-button.benchmark-empty {
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          border-color: #ffc107;
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover {
          background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
          border-color: #e0a800;
          color: #533f03;
        }

        .benchmark-edit-button.benchmark-empty .benchmark-icon {
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover .benchmark-icon {
          color: #533f03;
        }

        .benchmark-edit-button.benchmark-empty .edit-action-icon {
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover .edit-action-icon {
          color: #533f03;
        }

        .benchmark-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .benchmark-modal {
          background: white;
          border-radius: 8px;
          padding: 0;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e4e6ea;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1c1e21;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 18px;
          color: #65676b;
          cursor: pointer;
          padding: 4px;
          line-height: 1;
        }

        .modal-close:hover {
          color: #1c1e21;
        }

        .modal-body {
          padding: 16px 20px;
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #1c1e21;
          font-size: 14px;
        }

        .benchmark-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ccd0d5;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .benchmark-input:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.2);
        }

        .modal-footer {
          padding: 12px 20px;
          border-top: 1px solid #e4e6ea;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .save-benchmark-btn {
          background: #1877f2;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .save-benchmark-btn:hover:not(:disabled) {
          background: #166fe5;
        }

        .save-benchmark-btn:disabled {
          background: #ccd0d5;
          cursor: not-allowed;
        }

        .cancel-benchmark-btn {
          background: #e4e6ea;
          color: #1c1e21;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .cancel-benchmark-btn:hover {
          background: #d8dadf;
        }

        .delivery-cell {
          display: flex;
          align-items: center;
          min-width: 80px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #42b883;
        }

        .status-indicator.inactive .status-dot {
          background: #8a8d91;
        }

        .actions-cell,
        .attribution-cell {
          color: #65676b;
          font-size: 12px;
          max-width: 120px;
        }

        .results-cell {
          text-align: center;
          min-width: 80px;
        }

        .result-number {
          font-weight: 600;
          font-size: 14px;
          color: #1c1e21;
        }

        .result-type {
          font-size: 10px;
          color: #65676b;
          margin-top: 1px;
        }

        .metric-cell {
          text-align: right;
          font-weight: 500;
          min-width: 70px;
        }

        .cost-cell {
          text-align: right;
          min-width: 90px;
        }

        .cost-amount {
          font-weight: 600;
          color: #1c1e21;
          font-size: 13px;
        }

        .cost-type {
          font-size: 10px;
          color: #65676b;
          margin-top: 1px;
        }

        /* CPL Highlighting Styles */
        .cpl-highlight-red {
          background: linear-gradient(135deg, #ffeaea 0%, #ffcccc 100%);
          border-left: 3px solid #dc3545;
          position: relative;
        }

        .cpl-highlight-orange {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b3 100%);
          border-left: 3px solid #ff9800;
          position: relative;
        }

        .cost-warning {
          font-size: 9px;
          color: #dc3545;
          margin-top: 2px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .cpl-highlight-orange .cost-warning {
          color: #f57c00;
        }

        /* Row Highlighting Styles */
        .row-highlight-red {
          background: linear-gradient(135deg, #ffeaea 0%, #ffcccc 100%) !important;
          border-left: 4px solid #dc3545;
        }

        .row-highlight-orange {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b3 100%) !important;
          border-left: 4px solid #ff9800;
        }

        .row-highlight-red:hover,
        .row-highlight-orange:hover {
          opacity: 0.9;
        }

        /* Subtle animation for highlighted rows */
        .row-highlight-red,
        .row-highlight-orange {
          animation: highlightPulse 2s ease-in-out infinite;
        }

        @keyframes highlightPulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.95;
          }
        }

        .budget-cell {
          text-align: right;
          font-weight: 600;
          min-width: 80px;
        }

        .pagination-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background: #fafbfc;
          border-top: 1px solid #e4e6ea;
          font-size: 13px;
        }

        .pagination-info {
          color: #65676b;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pagination-button {
          background: white;
          border: 1px solid #ccd0d5;
          color: #1c1e21;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 32px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-button:hover:not(:disabled) {
          background: #f5f6f7;
          border-color: #8a8d91;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-button.active {
          background: #1877f2;
          color: white;
          border-color: #1877f2;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 16px;
        }

        .page-size-select {
          padding: 4px 8px;
          border: 1px solid #ccd0d5;
          border-radius: 4px;
          font-size: 12px;
          background: white;
        }

        .no-account-message {
          text-align: center;
          padding: 40px 20px;
          color: #65676b;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #65676b;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #f0f2f5;
          border-top: 2px solid #1877f2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .react-select-container .react-select__control {
          border: 1px solid rgba(255, 255, 255, 0.3);
          background-color: rgba(255, 255, 255, 0.1);
          min-height: 32px;
          border-radius: 4px;
        }

        .react-select-container .react-select__control:hover {
          border-color: rgba(255, 255, 255, 0.5);
        }

        .react-select-container .react-select__control--is-focused {
          border-color: white;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
        }

        .react-select-container .react-select__placeholder {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
        }

        .react-select-container .react-select__single-value {
          color: white;
          font-size: 13px;
        }

        .react-select-container .react-select__menu {
          z-index: 1000;
          border-radius: 4px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .react-select-container .react-select__option--is-focused {
          background-color: #f5f6f7;
        }

        .react-select-container .react-select__option--is-selected {
          background-color: #1877f2;
        }

        .sort-icon {
          margin-left: 4px;
          font-size: 10px;
        }

        /* Horizontal scroll styling */
        .meta-table-container::-webkit-scrollbar {
          height: 8px;
        }

        .meta-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .meta-table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }

        .meta-table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        @media (max-width: 768px) {
          .meta-ads-header {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
            padding: 10px 16px;
          }

          .toolbar {
            flex-wrap: wrap;
            padding: 8px 16px;
          }

          .search-container {
            margin-left: 0;
            margin-top: 8px;
          }

          .search-input {
            width: 150px;
          }

          .pagination-container {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }

          .pagination-controls {
            justify-content: center;
          }
        }

        .campaign-id-cell {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: #65676b;
          min-width: 150px;
          max-width: 200px;
          word-wrap: break-word;
          white-space: normal;
          line-height: 1.2;
        }

        .date-filter-container {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid #e4e6ea;
          padding: 12px 20px;
        }

        .date-filter-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;
          max-width: 1200px;
        }

        .date-filter-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #495057;
          min-width: 100px;
        }

        .date-filter-label i {
          color: #1877f2;
        }

        .date-filter-controls {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .date-preset-buttons {
          display: flex;
          gap: 4px;
          background: white;
          border-radius: 6px;
          padding: 2px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e4e6ea;
        }

        .date-preset-btn {
          background: transparent;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          color: #495057;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .date-preset-btn:hover {
          background: #f8f9fa;
          color: #1877f2;
        }

        .date-preset-btn.active {
          background: #1877f2;
          color: white;
          box-shadow: 0 1px 2px rgba(24, 119, 242, 0.2);
        }

        .custom-date-inputs {
          display: flex;
          gap: 12px;
          align-items: center;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #e4e6ea;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .date-input-group {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .date-input-group label {
          font-size: 12px;
          font-weight: 500;
          color: #495057;
          min-width: 35px;
        }

        .date-input {
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 12px;
          color: #495057;
          background: white;
          min-width: 120px;
        }

        .date-input:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.1);
        }

        .date-input-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .apply-filter-btn {
          background: #1877f2;
          color: white;
          border: none;
          padding: 6px 16px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .apply-filter-btn:hover:not(:disabled) {
          background: #166fe5;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(24, 119, 242, 0.2);
        }

        .apply-filter-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        @media (max-width: 768px) {
          .date-filter-wrapper {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .date-filter-controls {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .custom-date-inputs {
            flex-direction: column;
            gap: 8px;
          }

          .date-input-group {
            justify-content: space-between;
          }

          .date-input-row {
            flex-direction: column;
            gap: 8px;
          }

          .date-preset-buttons {
            flex-wrap: wrap;
            justify-content: center;
          }

          .date-preset-btn {
            flex: 1;
            min-width: 80px;
          }

          .apply-filter-btn {
            width: 100%;
            padding: 8px 16px;
            margin-top: 4px;
          }
        }

        .account-details-card {
          background: #fff;
          color: #1c1e21;
          margin: 0;
          position: relative;
          overflow: hidden;
          border-radius: 10px;
          border: 1px solid #e4e6ea;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
        }

        .account-details-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 100%
          );
          pointer-events: none;
        }

        .account-details-wrapper {
          padding: 10px 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          position: relative;
          z-index: 1;
        }

        .account-info-section {
          flex: 1;
        }

        .account-primary-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .account-name-status {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .account-name {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #1877f2;
          text-shadow: none;
        }

        .account-status {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 2px 8px 2px 6px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
          letter-spacing: 0.2px;
          background: #f5f5f5;
          color: #555;
          min-width: 70px;
          border: 1px solid #e4e6ea;
        }
        .account-status.active {
          background: #e6f9ed;
          color: #1a7f37;
          border: 1px solid #b6e7c9;
        }
        .account-status.disabled,
        .account-status.inactive {
          background: #fbeaea;
          color: #c0392b;
          border: 1px solid #f5c6cb;
        }
        .account-status i {
          font-size: 9px;
          margin-right: 2px;
        }

        .account-meta {
          display: flex;
          align-items: center;
        gap: 14px;
        font-size: 12px;
        opacity: 1;
        background: #f8f9fa;
        border-radius: 6px;
        padding: 7px 12px;
        margin-top: 8px;
        margin-bottom: 2px;
        box-shadow: 0 1px 2px rgba(24,119,242,0.04);
        }

        .account-id {
          font-family: inherit;
          background: none;
          padding: 0;
          border-radius: 0;
          font-weight: 500;
          color: inherit;
          display: flex;
          align-items: center;
        }
        .account-currency {
          background: none;
          color: inherit;
          border-radius: 0;
          padding: 0;
          display: flex;
          align-items: center;
          font-weight: 500;
        }
        .account-timezone {
          background: none;
          color: inherit;
          border-radius: 0;
          padding: 0;
          display: flex;
          align-items: center;
          font-weight: 500;
        }
        .account-client {
          background: #f6f0fa;
          color: #7c3aed;
          border-radius: 5px;
          padding: 2.5px 12px 2.5px 7px;
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(124,58,237,0.07);
          border: 1px solid #e1d5ec;
          gap: 4px;
          min-width: 90px;
          letter-spacing: 0.2px;
          transition: all 0.2s;
          position: relative;
          z-index: 1;
        }
        .account-client i {
          color: #8e44ad;
          font-size: 17px;
          margin-right: 6px;
          filter: drop-shadow(0 1px 2px #e1d5ec);
        }
        .account-client span {
          font-weight: 700;
          color: #7c3aed;
          font-size: 14px;
          letter-spacing: 0.2px;
          text-shadow: 0 1px 2px #f3e9fc;
          padding: 0 2px;
          border-radius: 3px;
          background: linear-gradient(90deg, #f6f0fa 60%, #ede9fe 100%);
          box-shadow: 0 1px 4px rgba(124,58,237,0.07);
          transition: all 0.2s;
        }
        .account-client span:empty:after {
          content: 'N/A';
          color: #aaa;
          background: none;
          text-shadow: none;
        }

        .account-stats-section {
          display: flex;
          gap: 10px;
          align-items: center;
          background: #fafbfc;
          border-radius: 7px;
          padding: 6px 10px;
          margin: 8px 0 0 0;
          box-sizing: border-box;
          border: 1px solid #ececec;
        .account-client {
          background: #f6f0fa;
          color: #7c3aed;
          border-radius: 5px;
          padding: 2.5px 12px 2.5px 7px;
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(124,58,237,0.07);
          border: 1px solid #e1d5ec;
          gap: 4px;
          min-width: 90px;
          letter-spacing: 0.2px;
          transition: all 0.2s;
          position: relative;
          z-index: 1;
        }
        .account-client i {
          color: #8e44ad;
          font-size: 17px;
          margin-right: 6px;
          filter: drop-shadow(0 1px 2px #e1d5ec);
        }
        .account-client span {
          font-weight: 700;
          color: #7c3aed;
          font-size: 14px;
          letter-spacing: 0.2px;
          text-shadow: 0 1px 2px #f3e9fc;
          padding: 0 2px;
          border-radius: 3px;
          background: linear-gradient(90deg, #f6f0fa 60%, #ede9fe 100%);
          box-shadow: 0 1px 4px rgba(124,58,237,0.07);
          transition: all 0.2s;
        }
        .account-client span:empty:after {
          content: 'N/A';
          color: #aaa;
          background: none;
          text-shadow: none;
        }

        .stat-item {
          text-align: center;
          min-width: 60px;
          background: #f7f7f8;
          border-radius: 5px;
          box-shadow: none;
          padding: 4px 5px 2px 5px;
          margin: 0 1px;
          border: 1px solid #ececec;
        }

        .stat-label {
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          opacity: 0.85;
          margin-bottom: 1px;
          font-weight: 600;
          color: #666;
        }

        .stat-value {
          font-size: 12px;
          font-weight: 700;
          color: #222;
          text-shadow: none;
        }

        .stat-value.business-name {
          font-size: 10px;
          font-weight: 600;
          color: #444;
          white-space: normal;
          max-width: none;
          overflow: visible;
          text-overflow: unset;
        }

        .stat-value.funding-source {
          font-size: 10px;
          font-weight: 600;
          color: #555;
          max-width: 70px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .stat-value.created-date {
          font-size: 9px;
          font-weight: 600;
          color: #888;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .account-details-wrapper {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
            padding: 12px 16px;
          }

          .account-name-status {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .account-name {
            font-size: 16px;
          }

          .account-meta {
            flex-wrap: wrap;
            gap: 8px;
          }

          .account-stats-section {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .stat-item {
            min-width: auto;
          }
        }

        @media (max-width: 480px) {
                      <span className='account-client' title='Client'>
                        <i className='fa fa-user-circle' style={{marginRight: 4, color: '#8e44ad', fontSize: 15}}></i>
                        <span style={{fontWeight: 600, color: selectedAccountDetails.clientName ? '#8e44ad' : '#aaa'}}>
                          {selectedAccountDetails.clientName || 'N/A'}
                        </span>
                      </span>
          }
        }

        /* Animation for smooth appearance */
        .account-details-card {
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Enhanced visual effects */
        .account-details-card {
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .stat-item {
          transition: transform 0.2s ease;
        }

        .stat-item:hover {
          transform: translateY(-2px);
        }

        .benchmark-cell {
          text-align: center;
          min-width: 80px;
        }

        .benchmark-editable {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          gap: 4px;
        }

        .benchmark-value {
          font-weight: 500;
          color: #1c1e21;
          font-size: 13px;
        }

        .edit-icon {
          font-size: 12px;
          color: #1877f2;
        }

        /* Modal styles */
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          max-width: 400px;
          width: 100%;
          position: relative;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .modal-title {
          font-size: 16px;
          font-weight: 600;
          color: #1c1e21;
          margin: 0;
        }

        .modal-close {
          position: absolute;
          top: 10px;
          right: 10px;
          cursor: pointer;
          font-size: 14px;
          color: #65676b;
        }

        .modal-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .benchmark-input-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .benchmark-input {
          flex: 1;
          padding: 8px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
          color: #495057;
          background: white;
        }

        .benchmark-input:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.1);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .save-benchmark-btn {
          background: #1877f2;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .save-benchmark-btn:hover {
          background: #166fe5;
        }

        .cancel-benchmark-btn {
          background: #e4e6ea;
          color: #1c1e21;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .cancel-benchmark-btn:hover {
          background: #d1d3d5;
        }

        .header-benchmark-pill-wrapper { margin-left:12px; }
        .header-benchmark-pill { display:inline-flex; align-items:center; gap:6px; padding:6px 14px 6px 12px; border-radius:28px; font-size:12px; font-weight:600; border:1px solid; position:relative; overflow:hidden; transition:all .25s ease; }
        .header-benchmark-pill.empty { background:#fffdf5; border-color:#ffd36a; color:#8a6d3b; box-shadow:0 0 0 1px rgba(255,211,106,.25),0 2px 6px rgba(255,211,106,.3); }
        .header-benchmark-pill.set { background:linear-gradient(135deg,#1863f2,#0f56d6); border-color:#0f56d6; color:#fff; box-shadow:0 2px 8px rgba(15,86,214,.45); }
        .header-benchmark-pill .hb-icon i { font-size:14px; }
        .header-benchmark-pill.empty .hb-icon i { color:#d99023; }
        .header-benchmark-pill.set .hb-icon i { color:#ffdd57; filter:drop-shadow(0 1px 2px rgba(0,0,0,.35)); }
        .header-benchmark-pill .hb-label { font-weight:500; opacity:.7; margin-right:2px; }
        .header-benchmark-pill .hb-value { font-weight:700; letter-spacing:.2px; }

      `}</style>

      <div className='meta-ads-container'>
        <div className='meta-ads-header'>
          <div className='header-left'>
            <h1 className='header-title'>
              <i
                className='fa fa-facebook-square'
                style={{ marginRight: '8px' }}
              ></i>
              Campaigns
            </h1>
          </div>
          <div className='account-selector-header'>
            <Select
              className='react-select-container'
              classNamePrefix='react-select'
              options={accountOptions}
              value={accountOptions.find((c) => c.value === account) || null}
              onChange={(selected) => {
                setAccount(selected ? selected.value : '');
                // Find and set the selected account details
                if (selected && accountData?.adAccounts) {
                  const accountDetails = accountData.adAccounts.find(
                    (acc) => acc.accountId === selected.value
                  );

                  setSelectedAccountDetails(accountDetails);
                } else {
                  setSelectedAccountDetails(null);
                }
              }}
              placeholder='Select Facebook Ads account...'
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              isLoading={isAccountsLoading}
            />
          </div>
        </div>

        <div className='meta-ads-content' style={{ position: 'relative' }}>
          {selectedAccountDetails && !isLoading && (
            <div className='account-details-card'>
              <div className='account-details-wrapper'>
                <div className='account-info-section'>
                  <div className='account-primary-info'>
                    <div className='account-name-status'>
                      <h3 className='account-name'>
                        {selectedAccountDetails.accountName}
                      </h3>
                      <span
                        className={`account-status ${selectedAccountDetails.status?.toLowerCase()}`}
                        title={selectedAccountDetails.status}
                      >
                        <i
                          className='fa fa-circle'
                          style={{
                            color:
                              selectedAccountDetails.status?.toLowerCase() ===
                              'active'
                                ? '#1a7f37'
                                : '#c0392b',
                          }}
                        ></i>
                        {selectedAccountDetails.status === 'active'
                          ? 'Active'
                          : selectedAccountDetails.status === 'disabled'
                          ? 'Disabled'
                          : selectedAccountDetails.status}
                      </span>
                    </div>
                    <div className='account-meta'>
                      <span className='account-id'>
                        ID: {selectedAccountDetails.accountId}
                      </span>
                      {/* <span className='account-currency'>
                        {selectedAccountDetails.currency}
                      </span>
                      <span className='account-timezone'>
                        {selectedAccountDetails.status}
                      </span> */}
                      <span className='account-client' title='Client'>
                        <i
                          className='fa fa-user-circle'
                          style={{
                            marginRight: 6,
                            color: '#8e44ad',
                            fontSize: 17,
                            filter: 'drop-shadow(0 1px 2px #e1d5ec)',
                          }}
                        ></i>
                        <span
                          style={{
                            fontWeight: 700,
                            color: '#7c3aed',
                            fontSize: 14,
                            letterSpacing: '0.2px',
                            marginRight: 4,
                          }}
                        >
                          Client:
                        </span>
                        <span
                          style={{
                            fontWeight: 700,
                            color: selectedAccountDetails.clientName
                              ? '#7c3aed'
                              : '#aaa',
                            fontSize: 14,
                            letterSpacing: '0.2px',
                            textShadow: selectedAccountDetails.clientName
                              ? '0 1px 2px #f3e9fc'
                              : 'none',
                            padding: '0 2px',
                            borderRadius: '3px',
                            background: selectedAccountDetails.clientName
                              ? 'linear-gradient(90deg, #f6f0fa 60%, #ede9fe 100%)'
                              : 'none',
                            boxShadow: selectedAccountDetails.clientName
                              ? '0 1px 4px rgba(124,58,237,0.07)'
                              : 'none',
                            transition: 'all 0.2s',
                          }}
                        >
                          {selectedAccountDetails.clientName || 'N/A'}
                        </span>
                      </span>

                      <span className='header-benchmark-pill-wrapper'>
                        <span
                          className={`header-benchmark-pill ${
                            selectedAccountDetails.fb_benchmark
                              ? 'set'
                              : 'empty'
                          }`}
                          title={
                            selectedAccountDetails.fb_benchmark
                              ? 'Account benchmark'
                              : 'No benchmark set'
                          }
                        >
                          <span className='hb-icon'>
                            <i className='fa fa-bullseye'></i>
                          </span>
                          <span className='hb-label'>Benchmark:</span>
                          <span className='hb-value'>
                            {selectedAccountDetails.fb_benchmark || 'N/A'}
                          </span>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className='account-stats-section'>
                  <div className='stat-item'>
                    <div className='stat-label'>Total Spent</div>
                    <div className='stat-value'>
                      {selectedAccountDetails.amountSpent || '₹0.00'}
                    </div>
                  </div>

                  <div className='stat-item'>
                    <div className='stat-label'>Business</div>
                    <div className='stat-value business-name'>
                      {selectedAccountDetails.businessName || 'N/A'}
                    </div>
                  </div>

                  <div className='stat-item'>
                    <div className='stat-label'>Payment Type</div>
                    <div className='stat-value funding-source'>
                      {selectedAccountDetails.paymentType || 'N/A'}
                    </div>
                  </div>

                  <div className='stat-item'>
                    <div className='stat-label'>Available Balance</div>
                    <div className='stat-value created-date'>
                      {selectedAccountDetails.availableBalance || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Overlay style for loading indicator */}

          {account ? (
            <>
              {account && (
                <>
                  <div className='date-filter-container'>
                    <div className='date-filter-wrapper'>
                      <div className='date-filter-label'>
                        <i className='fa fa-calendar'></i>
                        Date Range:
                      </div>
                      <div className='date-filter-controls'>
                        <div className='date-preset-buttons'>
                          <button
                            className={`date-preset-btn ${
                              dateFilter.rangeType === 'yesterday'
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => {
                              setDateFilter({
                                rangeType: 'yesterday',
                                from: '',
                                to: '',
                              });
                            }}
                          >
                            Yesterday
                          </button>
                          <button
                            className={`date-preset-btn ${
                              dateFilter.rangeType === 'last_7_days'
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => {
                              setDateFilter({
                                rangeType: 'last_7_days',
                                from: '',
                                to: '',
                              });
                            }}
                          >
                            7 Days
                          </button>
                          <button
                            className={`date-preset-btn ${
                              dateFilter.rangeType === 'last_14_days'
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => {
                              setDateFilter({
                                rangeType: 'last_14_days',
                                from: '',
                                to: '',
                              });
                            }}
                          >
                            14 Days
                          </button>
                          <button
                            className={`date-preset-btn ${
                              dateFilter.rangeType === 'last_30_days'
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => {
                              setDateFilter({
                                rangeType: 'last_30_days',
                                from: '',
                                to: '',
                              });
                            }}
                          >
                            30 Days
                          </button>
                          <button
                            className={`date-preset-btn ${
                              dateFilter.rangeType === 'custom' ? 'active' : ''
                            }`}
                            onClick={() => {
                              setDateFilter({
                                ...dateFilter,
                                rangeType: 'custom',
                              });
                            }}
                          >
                            Custom Range
                          </button>
                        </div>
                        {dateFilter.rangeType === 'custom' && (
                          <div className='custom-date-inputs'>
                            <div className='date-input-row'>
                              <div className='date-input-group'>
                                <label>From:</label>
                                <input
                                  type='date'
                                  className='date-input'
                                  value={dateFilter.from}
                                  onChange={(e) =>
                                    setDateFilter({
                                      ...dateFilter,
                                      from: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className='date-input-group'>
                                <label>To:</label>
                                <input
                                  type='date'
                                  className='date-input'
                                  value={dateFilter.to}
                                  onChange={(e) =>
                                    setDateFilter({
                                      ...dateFilter,
                                      to: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <button
                                className='apply-filter-btn'
                                onClick={applyCustomDateFilter}
                                disabled={!dateFilter.from || !dateFilter.to}
                              >
                                Apply Filter
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {isFetching && (
                    <div className='refresh-spinner-container'>
                      <div className='loading-spinner small'></div>
                      <span className='refresh-message'>
                        Table data is being refreshed in the background.
                      </span>
                    </div>
                  )}
                  <style jsx>{`
                    .refresh-spinner-container {
                      display: flex;
                      align-items: center;
                      gap: 12px;
                      background: #f8f9fa;
                      border: 1px solid #e4e6ea;
                      border-radius: 8px;
                      box-shadow: 0 2px 8px rgba(24, 119, 242, 0.07);
                      padding: 10px 18px;
                      margin: 18px 0 10px 0;
                      font-size: 15px;
                      .account-client {
                        background: #f6f0fa;
                        border-radius: 4px;
                        padding: 2px 10px 2px 7px;
                        display: flex;
                        align-items: center;
                        min-width: 80px;
                        font-size: 13px;
                        box-shadow: 0 1px 2px rgba(142, 68, 173, 0.04);
                      }
                      color: #166fe5;
                      font-weight: 500;
                      max-width: 420px;
                    }
                    .loading-spinner.small {
                      width: 18px;
                      height: 18px;
                      border-width: 2.5px;
                      margin-bottom: 0;
                    }
                    .refresh-message {
                      flex: 1;
                    }

                    .success-message-container {
                      display: flex;
                      justify-content: center;
                      margin: 12px 0;
                    }

                    .success-message {
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      background: linear-gradient(
                        135deg,
                        #d4edda 0%,
                        #c3e6cb 100%
                      );
                      color: #155724;
                      padding: 10px 20px;
                      border-radius: 6px;
                      border: 1px solid #c3e6cb;
                      font-size: 14px;
                      font-weight: 500;
                      box-shadow: 0 2px 8px rgba(21, 87, 36, 0.1);
                      animation: slideInDown 0.3s ease-out;
                    }

                    .success-message i {
                      color: #28a745;
                      font-size: 16px;
                    }

                    @keyframes slideInDown {
                      from {
                        opacity: 0;
                        transform: translateY(-20px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }
                  `}</style>
                </>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className='success-message-container'>
                  <div className='success-message'>
                    <i className='fa fa-check-circle'></i>
                    <span>{successMessage}</span>
                  </div>
                </div>
              )}

              <div className='meta-table-container'>
                {isLoading ? (
                  <div className='loading-container'>
                    <div className='loading-spinner'></div>
                    <p>Loading campaigns...</p>
                  </div>
                ) : (
                  <table className='meta-table'>
                    <thead className='meta-table-header'>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {header.column.getIsSorted() && (
                                <span className='sort-icon'>
                                  {header.column.getIsSorted() === 'desc' ? (
                                    <i className='fa fa-caret-down'></i>
                                  ) : (
                                    <i className='fa fa-caret-up'></i>
                                  )}
                                </span>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody className='meta-table-body'>
                      {table.getRowModel().rows.map((row) => {
                        const highlightClass = getRowHighlightClass(
                          row.original
                        );
                        return (
                          <tr key={row.id} className={highlightClass}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {table.getRowModel().rows.length === 0 && !isLoading && (
                  <div className='loading-container'>
                    <i
                      className='fa fa-search'
                      style={{
                        fontSize: '24px',
                        marginBottom: '12px',
                        opacity: 0.5,
                      }}
                    ></i>
                    <p>No campaigns found</p>
                  </div>
                )}
              </div>
              {table.getRowModel().rows.length > 0 && (
                <div className='pagination-container'>
                  <div className='pagination-info'>
                    Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      data?.total || 0
                    )}{' '}
                    of {data?.total || 0} results
                  </div>
                  <div className='pagination-controls'>
                    <button
                      className='pagination-button'
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <i className='fa fa-angle-double-left'></i>
                    </button>
                    <button
                      className='pagination-button'
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <i className='fa fa-angle-left'></i>
                    </button>

                    {Array.from(
                      { length: Math.min(5, table.getPageCount()) },
                      (_, i) => {
                        const pageIndex =
                          Math.max(
                            0,
                            table.getState().pagination.pageIndex - 2
                          ) + i;
                        if (pageIndex >= table.getPageCount()) return null;
                        return (
                          <button
                            key={pageIndex}
                            className={`pagination-button ${
                              pageIndex ===
                              table.getState().pagination.pageIndex
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => table.setPageIndex(pageIndex)}
                          >
                            {pageIndex + 1}
                          </button>
                        );
                      }
                    )}

                    <button
                      className='pagination-button'
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <i className='fa fa-angle-right'></i>
                    </button>
                    <button
                      className='pagination-button'
                      onClick={() =>
                        table.setPageIndex(table.getPageCount() - 1)
                      }
                      disabled={!table.getCanNextPage()}
                    >
                      <i className='fa fa-angle-double-right'></i>
                    </button>

                    <div className='page-size-selector'>
                      <span>Show:</span>
                      <select
                        className='page-size-select'
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                          table.setPageSize(Number(e.target.value));
                        }}
                      >
                        {[10, 15, 25, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {/* <AIChatbot campaigns={data?.data || []} /> */}
            </>
          ) : (
            <div className='no-account-message'>
              <i
                className='fa fa-info-circle'
                style={{
                  fontSize: '48px',
                  color: '#1877f2',
                  marginBottom: '16px',
                }}
              ></i>
              <p>Please select a Facebook Ads account to view campaigns</p>
            </div>
          )}
        </div>

        {/* Benchmark Edit Modal */}
        {benchmarkModal.isOpen && (
          <div
            className='benchmark-modal-overlay'
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setBenchmarkModal({ isOpen: false, campaign: null });
              }
            }}
          >
            <div className='benchmark-modal'>
              <div className='modal-header'>
                <h2 className='modal-title'>
                  {isAccountBenchmarkEdit
                    ? 'Edit Account Benchmark'
                    : 'Edit Benchmark'}
                </h2>
                <button
                  className='modal-close'
                  onClick={() =>
                    setBenchmarkModal({ isOpen: false, campaign: null })
                  }
                >
                  &times;
                </button>
              </div>
              <div className='modal-body'>
                <div className='form-group'>
                  <label htmlFor='benchmark' className='form-label'>
                    Campaign:{' '}
                    {benchmarkModal.campaign?.campaign_name ||
                      benchmarkModal.campaign?.campaign_id}
                  </label>
                </div>
                <div className='form-group'>
                  <label htmlFor='benchmark' className='form-label'>
                    Benchmark Value:
                  </label>
                  <input
                    id='benchmark'
                    type='text'
                    className='benchmark-input'
                    value={benchmarkValue}
                    onChange={(e) => setBenchmarkValue(e.target.value)}
                    placeholder='Enter benchmark value'
                    autoFocus
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='save-benchmark-btn'
                  onClick={async () => {
                    if (!benchmarkModal.campaign) return;
                    setIsSavingBenchmark(true);
                    try {
                      const cid =
                        isAccountBenchmarkEdit &&
                        benchmarkModal.campaign.campaign_id === '__ACCOUNT__'
                          ? 'ACCOUNT_LEVEL'
                          : benchmarkModal.campaign.campaign_id;
                      await updateBenchmark(account, cid, benchmarkValue);
                      queryClient.invalidateQueries(['facebook-ads-reports']);
                      setSuccessMessage('Benchmark updated successfully!');
                      setBenchmarkModal({ isOpen: false, campaign: null });
                      setBenchmarkValue('');
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Failed to update benchmark:', error);
                      alert('Failed to update benchmark. Please try again.');
                    } finally {
                      setIsSavingBenchmark(false);
                      setIsAccountBenchmarkEdit(false);
                    }
                  }}
                  disabled={isSavingBenchmark}
                >
                  {isSavingBenchmark ? 'Saving...' : 'Save'}
                </button>
                <button
                  className='cancel-benchmark-btn'
                  onClick={() => {
                    setBenchmarkModal({ isOpen: false, campaign: null });
                    setIsAccountBenchmarkEdit(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EnhancedFacebookAdsTable;
